import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getReviewSchema } from '@zola-helpers/client/dist/es/schema';

import { VendorCardVendor } from '~/components/common/cards/util/types';
import {
  getPricingCardDetailMeta,
  getVendorCardPriceMetaFields,
} from '~/components/common/cards/VendorCard/components/CardDetails';

import getImageUrl from './imageUtils';
import { getAbsoluteUrl } from './seoUtils';
import getCanonicalUrl from './storefrontUrl';

/**
 * Get the schema.org representation of a wedding vendor.
 *
 * When changing this, its important to look at both schema.org and, perhaps more
 * importantly, the Google Rich Results Test.  What google requires and supports
 * is a subset of the whole LocalBusiness Schema.
 *
 * @see https://schema.org/LocalBusiness
 * @see https://developers.google.com/search/docs/appearance/structured-data/local-business
 *
 *
 * Some other references:
 * - https://schema.org/Restaurant
 * - https://schema.org/FoodEstablishment
 * - https://schema.org/Florist
 * - https://schema.org/EventVenue
 *
 */
export const getWeddingVendorSchema = (vendor: VendorCardVendor) => {
  const coverPhoto = vendor.storefrontCoverId ? getImageUrl(vendor.storefrontCoverId) : undefined;

  const priceMeta = getPricingCardDetailMeta(getVendorCardPriceMetaFields({ vendor }));

  return {
    '@context': 'https://schema.org/',
    '@type': 'LocalBusiness',
    name: vendor.vendorName,
    description: vendor.description,

    // This does use the physical address of the vendor.  Maybe not ideal for
    // photographers or some service providers, but its great for venues and
    // probably florists/beauticians.
    address: {
      '@type': 'PostalAddress',
      streetAddress: vendor.address1,
      addressLocality: vendor.city,
      addressRegion: vendor.stateProvince,
      postalCode: vendor.postalCode,
      addressCountry: 'US',
    },

    // could add areaServed
    // https://schema.org/areaServed from https://schema.org/Organization
    // areaServed: ' some market text',

    // Vendor's URL on zola
    url: getAbsoluteUrl(
      getCanonicalUrl(vendor.taxonomyNodeKey as VendorTaxonomyKey, vendor.storefrontSlug as string)
    ),

    // Cover photo
    logo: coverPhoto,
    image: coverPhoto,
    ...((vendor.reviewsCount || 0) > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingCount: vendor.reviewsCount,
            reviewCount: vendor.reviewsCount,
            bestRating: 5,
            worstRating: 1,
            ratingValue: vendor.averageReviewsRate,
          },
        }
      : {}),
    // Starts at <price>
    ...(priceMeta?.label ? { priceRange: priceMeta.label.trim() } : {}),

    review: vendor.featuredReview
      ? {
          ...getReviewSchema({
            reviewBody: vendor.featuredReview.reviewText,

            reviewRating: {
              '@type': 'Rating',
              ratingValue: vendor.featuredReview.overallRating,
              bestRating: 5,
              worstRating: 1,
              author: {
                '@type': 'Person',
                name: vendor.featuredReview.reviewerName,
              },
            },
          }),
          author: {
            '@type': 'Person',
            name: vendor.featuredReview.reviewerName,
          },
        }
      : undefined,
  };
};
